/**
 * Normalized organization settings shape consumed by PermissionProvider and admin UI.
 * Maps to/from public.organizations rows via organizationRowToAppSettings().
 */

function normalizeOrgPaymentInstructions(raw) {
  const source = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  return {
    preferredMethod: String(source.preferredMethod || "").trim(),
    paymentEmail: String(source.paymentEmail || "").trim(),
    additionalInstructions: String(source.additionalInstructions || "").trim(),
    customMethodLabel: String(source.customMethodLabel || "").trim(),
  };
}

function derivePaymentTermsPresetFromDays(days) {
  const value = Number(days);
  if (value === 0) return "due_on_receipt";
  if (value === 7) return "net_7";
  if (value === 15) return "net_15";
  if (value === 30) return "net_30";
  if (value === 45) return "net_45";
  if (value === 60) return "net_60";
  if (Number.isFinite(value) && value >= 0) return "custom";
  return "net_30";
}

export const DEFAULT_ORG_SETTINGS = Object.freeze({
  guest_mode_enabled: true,
  payroll_self_service: true,
  finance_manager_access: false,
  modules: {
    finance: {
      enabled: true,
      manager_access: false,
      default_invoice_deposit_account_id: null,
    },
  },
  organization: {
    name: "",
    legal_name: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    timezone: "America/Toronto",
    locale: "en-CA",
    currency: "CAD",
    business_number: "",
    gst_hst_registration_number: "",
    qst_registration_number: "",
    additional_registration_label: "",
    additional_registration_number: "",
  },
  branding: {
    logo_url: null,
    primary_color: "#c9a84c",
  },
  tax: {},
  invoices: {
    default_payment_terms_days: 30,
    default_payment_terms_preset: "net_30",
    default_customer_notes: "",
    default_invoice_footer: "",
    payment_instructions: {
      preferredMethod: "",
      paymentEmail: "",
      additionalInstructions: "",
      customMethodLabel: "",
    },
  },
  payroll: {
    week_starts_on: "sunday",
    weekly_overtime: {
      enabled: true,
      threshold_hours: 40,
      multiplier: 1.5,
    },
  },
});

export function deepMergeSettings(base, patch) {
  if (!patch || typeof patch !== "object") {
    return base;
  }

  const result = { ...base };

  for (const key of Object.keys(patch)) {
    const value = patch[key];
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      base[key] &&
      typeof base[key] === "object"
    ) {
      result[key] = deepMergeSettings(base[key], value);
    } else {
      result[key] = value;
    }
  }

  return result;
}

export function mergeAppSettings(base, patch) {
  return deepMergeSettings(base || DEFAULT_ORG_SETTINGS, patch);
}

/**
 * Dot-path read with fallback. Example: getEffectiveOrgSetting(s, "organization.name", "")
 */
export function getEffectiveOrgSetting(settings, path, fallback = undefined) {
  const source = settings || DEFAULT_ORG_SETTINGS;
  const parts = String(path).split(".");
  let cursor = source;

  for (const part of parts) {
    if (cursor == null || typeof cursor !== "object") {
      return fallback;
    }
    cursor = cursor[part];
  }

  return cursor === undefined ? fallback : cursor;
}

/**
 * Map a database organizations row to the in-app settings object.
 */
export function organizationRowToAppSettings(row) {
  if (!row) {
    return { ...DEFAULT_ORG_SETTINGS };
  }

  const rowSettings =
    row.settings && typeof row.settings === "object" ? row.settings : {};

  return mergeAppSettings(DEFAULT_ORG_SETTINGS, {
    guest_mode_enabled:
      rowSettings.guest_mode_enabled !== undefined
        ? rowSettings.guest_mode_enabled
        : true,
    payroll_self_service:
      rowSettings.payroll_self_service !== undefined
        ? rowSettings.payroll_self_service
        : true,
    finance_manager_access:
      rowSettings.finance_manager_access === true ||
      rowSettings.modules?.finance?.manager_access === true,
    modules: {
      finance: {
        enabled:
          rowSettings.modules?.finance?.enabled == null
            ? DEFAULT_ORG_SETTINGS.modules.finance.enabled
            : rowSettings.modules.finance.enabled === true,
        manager_access:
          rowSettings.modules?.finance?.manager_access === true ||
          rowSettings.finance_manager_access === true,
        default_invoice_deposit_account_id:
          rowSettings.modules?.finance?.default_invoice_deposit_account_id ||
          null,
      },
    },
    organization: {
      name: row.company_name || "",
      legal_name: row.legal_name || "",
      address: row.address || "",
      phone: row.phone || "",
      email: row.email || "",
      website: row.website || "",
      timezone: row.timezone || "America/Toronto",
      locale: row.locale || "en-CA",
      currency: row.currency || "CAD",
      business_number: row.business_number || "",
      gst_hst_registration_number: row.gst_hst_registration_number || "",
      qst_registration_number: row.qst_registration_number || "",
      additional_registration_label: row.additional_registration_label || "",
      additional_registration_number: row.additional_registration_number || "",
    },
    branding: {
      logo_url: row.logo_url || rowSettings.branding?.logo_url || null,
      primary_color:
        rowSettings.branding?.primary_color ||
        DEFAULT_ORG_SETTINGS.branding.primary_color,
    },
    tax:
      row.tax_settings && typeof row.tax_settings === "object"
        ? row.tax_settings
        : {},
    invoices: {
      default_payment_terms_days:
        rowSettings.invoices?.default_payment_terms_days ?? 30,
      default_payment_terms_preset:
        rowSettings.invoices?.default_payment_terms_preset ||
        derivePaymentTermsPresetFromDays(
          rowSettings.invoices?.default_payment_terms_days ?? 30,
        ),
      default_customer_notes: String(
        rowSettings.invoices?.default_customer_notes || "",
      ).trim(),
      default_invoice_footer: String(
        rowSettings.invoices?.default_invoice_footer || "",
      ).trim(),
      payment_instructions: normalizeOrgPaymentInstructions(
        rowSettings.invoices?.payment_instructions,
      ),
    },
    // Organization Work Schedule (settings.work_schedule). null = loaded and never
    // configured (the app uses the documented default); a missing key on the
    // in-app settings object = not known yet (see helpers/tasks/workSchedule.js).
    // Saved only through saveOrganizationWorkSchedule, never by the generic
    // settings patch below (which keeps whatever the row already has).
    work_schedule:
      rowSettings.work_schedule === undefined || rowSettings.work_schedule === null
        ? null
        : rowSettings.work_schedule,
    payroll: {
      week_starts_on:
        rowSettings.payroll?.week_starts_on ??
        rowSettings.payroll?.week_start_day ??
        DEFAULT_ORG_SETTINGS.payroll.week_starts_on,
      weekly_overtime: {
        enabled:
          rowSettings.payroll?.weekly_overtime?.enabled ??
          DEFAULT_ORG_SETTINGS.payroll.weekly_overtime.enabled,
        threshold_hours:
          rowSettings.payroll?.weekly_overtime?.threshold_hours ??
          rowSettings.payroll?.overtime?.weekly_threshold_hours ??
          DEFAULT_ORG_SETTINGS.payroll.weekly_overtime.threshold_hours,
        multiplier:
          rowSettings.payroll?.weekly_overtime?.multiplier ??
          rowSettings.payroll?.overtime?.multiplier ??
          DEFAULT_ORG_SETTINGS.payroll.weekly_overtime.multiplier,
      },
    },
  });
}

/**
 * Build a Supabase update payload from an app-settings patch and current row.
 */
export function appSettingsPatchToDbUpdate(patch, currentRow) {
  const merged = mergeAppSettings(
    organizationRowToAppSettings(currentRow),
    patch,
  );

  const currentSettings =
    currentRow?.settings && typeof currentRow.settings === "object"
      ? currentRow.settings
      : {};

  const settings = {
    ...currentSettings,
    guest_mode_enabled: merged.guest_mode_enabled,
    payroll_self_service: merged.payroll_self_service,
    finance_manager_access: merged.finance_manager_access === true,
    modules: {
      ...(currentSettings.modules && typeof currentSettings.modules === "object"
        ? currentSettings.modules
        : {}),
      finance: {
        enabled: merged.modules?.finance?.enabled === true,
        manager_access:
          merged.modules?.finance?.manager_access === true ||
          merged.finance_manager_access === true,
        default_invoice_deposit_account_id:
          merged.modules?.finance?.default_invoice_deposit_account_id || null,
      },
    },
    branding: merged.branding,
    invoices: merged.invoices,
    payroll: merged.payroll,
  };

  return {
    company_name: merged.organization.name || currentRow?.company_name || "",
    legal_name: merged.organization.legal_name || null,
    logo_url: merged.branding.logo_url || null,
    address: merged.organization.address || null,
    phone: merged.organization.phone || null,
    email: merged.organization.email || null,
    website: merged.organization.website || null,
    currency: merged.organization.currency || "CAD",
    timezone: merged.organization.timezone || "America/Toronto",
    locale: merged.organization.locale || "en-CA",
    business_number: merged.organization.business_number || null,
    gst_hst_registration_number:
      merged.organization.gst_hst_registration_number || null,
    qst_registration_number: merged.organization.qst_registration_number || null,
    additional_registration_label:
      merged.organization.additional_registration_label || null,
    additional_registration_number:
      merged.organization.additional_registration_number || null,
    tax_settings:
      merged.tax && typeof merged.tax === "object" ? merged.tax : {},
    settings,
  };
}
